/**
 * Markdown as a tree, for the Chat (TranscriptView), where Claude's replies
 * and the prompts typed at it are markdown. Hand-rolled, like shared/diff.ts: the
 * page draws the tree with Vue (`Markdown.vue`), never as HTML, so nothing in a
 * transcript can put markup on the page — raw HTML in the text stays text.
 *
 * It covers what Claude writes: headings, paragraphs, fenced code, lists (nested,
 * task boxes), quotes, pipe tables, rules; inline code, bold, italic, strike,
 * links and bare URLs. Not CommonMark: no setext headings, indented code, link
 * references or HTML. A newline inside a paragraph is kept as a line break, as a
 * chat shows it.
 */

export type MdInline =
  | string
  | { t: "code"; text: string }
  | { t: "b" | "i" | "s"; c: MdInline[] }
  /** `href` is null for a link that is not http(s) or mailto: drawn as its text. */
  | { t: "a"; href: string | null; c: MdInline[] }
  | { t: "br" };

export type MdAlign = "left" | "center" | "right" | null;

export type MdBlock =
  | { t: "p"; c: MdInline[] }
  | { t: "h"; level: number; c: MdInline[] }
  | { t: "code"; lang: string; text: string }
  | { t: "quote"; c: MdBlock[] }
  | { t: "list"; ordered: boolean; start: number; items: MdItem[] }
  | { t: "table"; align: MdAlign[]; head: MdInline[][]; rows: MdInline[][][] }
  | { t: "hr" };

export interface MdItem {
  /** A task list's box: `- [x]`. */
  checked?: boolean;
  c: MdBlock[];
}

const FENCE = /^( {0,3})(`{3,}|~{3,})\s*([^`\s]*)/;
const HEADING = /^ {0,3}(#{1,6})(?:[ \t]+(.*?))?(?:[ \t]+#+)?[ \t]*$/;
const RULE = /^ {0,3}([-*_])(?:[ \t]*\1){2,}[ \t]*$/;
const QUOTE = /^ {0,3}> ?/;
const ITEM = /^( {0,3})([-*+]|\d{1,9}[.)])([ \t]+|$)/;
const TABLE_SEP = /^ {0,3}\|?[ \t]*:?-+:?[ \t]*(\|[ \t]*:?-+:?[ \t]*)*\|?[ \t]*$/;

const blank = (line: string): boolean => !line.trim();

/** Leading columns, a tab counting four. */
function columns(line: string): number {
  let n = 0;
  for (const ch of line) {
    if (ch === " ") n++;
    else if (ch === "\t") n += 4 - (n % 4);
    else break;
  }
  return n;
}

/** The line with up to `n` columns of indent taken off. */
function dedent(line: string, n: number): string {
  let col = 0;
  let i = 0;
  while (i < line.length && col < n) {
    if (line[i] === " ") col++;
    else if (line[i] === "\t") col += 4 - (col % 4);
    else break;
    i++;
  }
  return line.slice(i);
}

const isTableStart = (lines: string[], i: number): boolean =>
  (lines[i] ?? "").includes("|") && TABLE_SEP.test(lines[i + 1] ?? "") && (lines[i + 1] ?? "").includes("-") && ((lines[i + 1] ?? "").includes("|") || (lines[i] ?? "").trim().startsWith("|"));

/** Whether the line begins a block that cuts a paragraph short. */
function startsBlock(lines: string[], i: number): boolean {
  const line = lines[i] ?? "";
  if (FENCE.test(line) || HEADING.test(line) || RULE.test(line) || QUOTE.test(line) || isTableStart(lines, i)) return true;
  const m = ITEM.exec(line);
  // An ordered list interrupts only from 1, and an empty item never does, so a
  // "2019." starting a wrapped line stays text.
  return !!m && line.slice(m[0].length).trim() !== "" && (!/\d/.test(m[2]!) || /^1[.)]$/.test(m[2]!));
}

/** A table row's cells: split on pipes not escaped or inside backticks. */
function cells(line: string): string[] {
  let s = line.trim();
  if (s.startsWith("|")) s = s.slice(1);
  if (s.endsWith("|") && !s.endsWith("\\|")) s = s.slice(0, -1);
  const out: string[] = [];
  let cur = "";
  let tick = false;
  for (let i = 0; i < s.length; i++) {
    const ch = s[i]!;
    if (ch === "\\" && s[i + 1] === "|") {
      cur += "|";
      i++;
    } else if (ch === "`") {
      tick = !tick;
      cur += ch;
    } else if (ch === "|" && !tick) {
      out.push(cur.trim());
      cur = "";
    } else cur += ch;
  }
  out.push(cur.trim());
  return out;
}

function parseBlocks(lines: string[]): MdBlock[] {
  const out: MdBlock[] = [];
  let i = 0;
  while (i < lines.length) {
    const line = lines[i]!;
    if (blank(line)) {
      i++;
      continue;
    }

    const fence = FENCE.exec(line);
    if (fence) {
      const [, indent, mark, lang] = fence;
      const body: string[] = [];
      i++;
      while (i < lines.length) {
        const l = lines[i]!;
        const close = /^ {0,3}(`{3,}|~{3,})[ \t]*$/.exec(l);
        if (close && close[1]![0] === mark![0] && close[1]!.length >= mark!.length) {
          i++;
          break;
        }
        body.push(dedent(l, indent!.length));
        i++;
      }
      out.push({ t: "code", lang: lang ?? "", text: body.join("\n") });
      continue;
    }

    const heading = HEADING.exec(line);
    if (heading) {
      out.push({ t: "h", level: heading[1]!.length, c: parseInline(heading[2] ?? "") });
      i++;
      continue;
    }

    if (RULE.test(line)) {
      out.push({ t: "hr" });
      i++;
      continue;
    }

    if (QUOTE.test(line)) {
      const body: string[] = [];
      while (i < lines.length && QUOTE.test(lines[i]!)) body.push(lines[i++]!.replace(QUOTE, ""));
      out.push({ t: "quote", c: parseBlocks(body) });
      continue;
    }

    if (isTableStart(lines, i)) {
      const head = cells(line);
      const align = cells(lines[i + 1]!).map((c): MdAlign => (c.startsWith(":") && c.endsWith(":") ? "center" : c.endsWith(":") ? "right" : c.startsWith(":") ? "left" : null));
      const rows: MdInline[][][] = [];
      i += 2;
      while (i < lines.length && !blank(lines[i]!) && lines[i]!.includes("|")) {
        const row = cells(lines[i++]!);
        rows.push(head.map((_, k) => parseInline(row[k] ?? "")));
      }
      out.push({ t: "table", align: head.map((_, k) => align[k] ?? null), head: head.map((c) => parseInline(c)), rows });
      continue;
    }

    const item = ITEM.exec(line);
    if (item) {
      const ordered = /\d/.test(item[2]!);
      const kind = ordered ? item[2]!.slice(-1) : item[2]!;
      const items: MdItem[] = [];
      let m: RegExpExecArray | null = item;
      while (m) {
        const lead = columns(m[1]!) + m[2]!.length;
        const gap = m[3]!.replace(/\t/g, "    ").length;
        // Five spaces or more after the marker: the item starts one space in.
        const width = lead + (gap === 0 || gap > 4 ? 1 : gap);
        const body = [lines[i]!.slice(m[0].length - (gap > 4 ? gap - 1 : 0))];
        i++;
        while (i < lines.length) {
          const l = lines[i]!;
          if (blank(l)) {
            body.push("");
            i++;
            continue;
          }
          if (columns(l) >= width) {
            body.push(dedent(l, width));
            i++;
            continue;
          }
          // A lazy continuation of the item's paragraph.
          if (!blank(body[body.length - 1]!) && !startsBlock(lines, i) && !ITEM.test(l)) {
            body.push(l.trimStart());
            i++;
            continue;
          }
          break;
        }
        while (body.length && blank(body[body.length - 1]!)) body.pop();
        let checked: boolean | undefined;
        const box = /^\[([ xX])\](?:[ \t]+|$)/.exec(body[0]!);
        if (box) {
          checked = box[1] !== " ";
          body[0] = body[0]!.slice(box[0].length);
        }
        items.push({ ...(checked === undefined ? {} : { checked }), c: parseBlocks(body) });

        // The next item, if it is one of this list's.
        let j = i;
        while (j < lines.length && blank(lines[j]!)) j++;
        const next = j < lines.length ? ITEM.exec(lines[j]!) : null;
        const nextKind = next ? (/\d/.test(next[2]!) ? next[2]!.slice(-1) : next[2]!) : null;
        if (next && nextKind === kind && /\d/.test(next[2]!) === ordered) {
          i = j;
          m = next;
        } else m = null;
      }
      out.push({ t: "list", ordered, start: ordered ? parseInt(item[2]!, 10) : 1, items });
      continue;
    }

    const para: string[] = [line.trim()];
    i++;
    while (i < lines.length && !blank(lines[i]!) && !startsBlock(lines, i)) para.push(lines[i++]!.trim());
    out.push({ t: "p", c: parseInline(para.join("\n")) });
  }
  return out;
}

/** A markdown document's blocks. */
export function parseMarkdown(text: string): MdBlock[] {
  return parseBlocks(text.replace(/\r\n?/g, "\n").split("\n"));
}

// ---- inline ---------------------------------------------------------------

/** A run of `*`, `_` or `~` that may open or close emphasis, until matched. */
interface Delim {
  d: "*" | "_" | "~";
  n: number;
  open: boolean;
  close: boolean;
}
type Item = MdInline | Delim;

const isDelim = (x: Item): x is Delim => typeof x === "object" && "d" in x;
const PUNCT = /[!-/:-@[-`{-~\p{P}\p{S}]/u;
const SPACE = /\s/;
const WORD = /[\p{L}\p{N}]/u;

/** A URL a link may go to: the web and mail, never `javascript:` and the like. */
export function safeHref(url: string): string | null {
  return /^(https?:\/\/|mailto:)/i.test(url.trim()) ? url.trim() : null;
}

/** The end of a bare URL starting at `i`, trailing punctuation and an unmatched `)` left out. */
function urlEnd(s: string, i: number): number {
  let j = i;
  while (j < s.length && !/[\s<>`"]/.test(s[j]!)) j++;
  for (;;) {
    const last = s[j - 1]!;
    if (/[.,;:!?'*_~]/.test(last)) j--;
    else if (last === ")") {
      const url = s.slice(i, j);
      if ((url.match(/\(/g)?.length ?? 0) < (url.match(/\)/g)?.length ?? 0)) j--;
      else break;
    } else break;
  }
  return j;
}

/** `[text](url)` at `i`: the text, the url and where it ends, or null. */
function linkAt(s: string, i: number): { text: string; url: string; end: number } | null {
  let depth = 0;
  let j = i;
  for (; j < s.length; j++) {
    if (s[j] === "\\") j++;
    else if (s[j] === "[") depth++;
    else if (s[j] === "]" && --depth === 0) break;
    else if (s[j] === "`") {
      const close = s.indexOf("`", j + 1);
      if (close > 0) j = close;
    }
  }
  if (j >= s.length || s[j + 1] !== "(") return null;
  let k = j + 2;
  let parens = 0;
  for (; k < s.length; k++) {
    if (s[k] === "(") parens++;
    else if (s[k] === ")" && parens-- === 0) break;
    else if (s[k] === "\n") return null;
  }
  if (k >= s.length) return null;
  const dest = s.slice(j + 2, k).trim();
  // The destination, then an optional "title", which is dropped.
  const url = /^<([^>]*)>/.exec(dest)?.[1] ?? dest.split(/\s+/)[0] ?? "";
  return { text: s.slice(i + 1, j), url, end: k + 1 };
}

/** A line of markdown's inline content. */
export function parseInline(s: string): MdInline[] {
  const items: Item[] = [];
  let text = "";
  const flush = (): void => {
    if (text) items.push(text);
    text = "";
  };
  let i = 0;
  while (i < s.length) {
    const ch = s[i]!;
    if (ch === "\\" && i + 1 < s.length && /[!-/:-@[-`{-~]/.test(s[i + 1]!)) {
      text += s[i + 1];
      i += 2;
      continue;
    }
    if (ch === "\n") {
      flush();
      items.push({ t: "br" });
      i++;
      continue;
    }
    if (ch === "`") {
      let n = 1;
      while (s[i + n] === "`") n++;
      const tick = "`".repeat(n);
      let close = s.indexOf(tick, i + n);
      while (close >= 0 && s[close + n] === "`") {
        let m = close;
        while (s[m] === "`") m++;
        close = s.indexOf(tick, m);
      }
      if (close < 0) {
        text += tick;
        i += n;
        continue;
      }
      let code = s.slice(i + n, close).replace(/\n/g, " ");
      if (code.length > 2 && code.startsWith(" ") && code.endsWith(" ") && code.trim()) code = code.slice(1, -1);
      flush();
      items.push({ t: "code", text: code });
      i = close + n;
      continue;
    }
    if (ch === "[") {
      const link = linkAt(s, i);
      if (link) {
        flush();
        items.push({ t: "a", href: safeHref(link.url), c: parseInline(link.text) });
        i = link.end;
        continue;
      }
    }
    if (ch === "<") {
      const auto = /^<((?:https?:\/\/|mailto:)[^\s<>]+)>/i.exec(s.slice(i));
      if (auto) {
        flush();
        items.push({ t: "a", href: auto[1]!, c: [auto[1]!] });
        i += auto[0].length;
        continue;
      }
    }
    if ((ch === "h" || ch === "H") && /^https?:\/\/[^\s<>]/i.test(s.slice(i, i + 9)) && !WORD.test(s[i - 1] ?? "")) {
      const end = urlEnd(s, i);
      const url = s.slice(i, end);
      flush();
      items.push({ t: "a", href: url, c: [url] });
      i = end;
      continue;
    }
    if (ch === "*" || ch === "_" || ch === "~") {
      let n = 1;
      while (s[i + n] === ch) n++;
      const before = s[i - 1] ?? " ";
      const after = s[i + n] ?? " ";
      const left = !SPACE.test(after) && (!PUNCT.test(after) || SPACE.test(before) || PUNCT.test(before));
      const right = !SPACE.test(before) && (!PUNCT.test(before) || SPACE.test(after) || PUNCT.test(after));
      // An underscore inside a word (snake_case) is never emphasis.
      const open = ch === "_" ? left && !WORD.test(before) : left;
      const close = ch === "_" ? right && !WORD.test(after) : right;
      flush();
      items.push({ d: ch, n, open, close });
      i += n;
      continue;
    }
    text += ch;
    i++;
  }
  flush();
  return emphasis(items);
}

/** Pairs the delimiter runs into bold, italic and strike, each closer with the
 *  nearest opener of its kind before it; what is left over is text. */
function emphasis(items: Item[]): MdInline[] {
  let i = 0;
  while (i < items.length) {
    const c = items[i]!;
    if (!isDelim(c) || !c.close || c.n === 0 || (c.d === "~" && c.n < 2)) {
      i++;
      continue;
    }
    let j = i - 1;
    for (; j >= 0; j--) {
      const o = items[j]!;
      if (isDelim(o) && o.d === c.d && o.open && o.n > 0 && (c.d !== "~" || o.n >= 2)) break;
    }
    if (j < 0) {
      i++;
      continue;
    }
    const o = items[j] as Delim;
    const use = c.d === "~" || (o.n >= 2 && c.n >= 2) ? 2 : 1;
    const node: MdInline = { t: c.d === "~" ? "s" : use === 2 ? "b" : "i", c: flatten(items.slice(j + 1, i)) };
    o.n -= use;
    c.n -= use;
    items.splice(j + 1, i - j - 1, node);
    i = j + 2;
  }
  return flatten(items);
}

/** Unmatched delimiters back to text, and neighbouring text joined. */
function flatten(items: Item[]): MdInline[] {
  const out: MdInline[] = [];
  for (const x of items) {
    const v = isDelim(x) ? x.d.repeat(x.n) : x;
    if (v === "") continue;
    if (typeof v === "string" && typeof out[out.length - 1] === "string") out[out.length - 1] += v;
    else out.push(v);
  }
  return out;
}

